import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  generateSitemap,
  getStoredGames
} from '../../api/gameHistory';
import {
  fetchCompletedSeasonGames,
  getGameSeason,
  mergeSeasonGames
} from '../../api/seasonHistory';
import { getGames } from '../../services/gameService';
import GameHistoryCard from './GameHistoryCard';

const {
  FiArrowLeft,
  FiCalendar,
  FiDatabase,
  FiDownload,
  FiExternalLink,
  FiRefreshCw
} = FiIcons;

const TEAM_START_YEAR = 1901;

const GameHistoryPage = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [season, setSeason] = useState(currentYear);
  const [games, setGames] = useState([]);
  const [sheetGames, setSheetGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [error, setError] = useState('');

  const seasons = useMemo(
    () => Array.from(
      { length: currentYear - TEAM_START_YEAR + 1 },
      (_, index) => currentYear - index
    ),
    [currentYear]
  );

  useEffect(() => {
    let active = true;

    const loadStoredHistory = async () => {
      const localGames = getStoredGames();

      try {
        const backendGames = await getGames();
        if (active) setSheetGames(mergeSeasonGames(localGames, backendGames));
      } catch {
        if (active) setSheetGames(localGames);
      }
    };

    loadStoredHistory();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadSeason = async () => {
      setLoading(true);
      setError('');

      try {
        const scheduleGames = await fetchCompletedSeasonGames(
          season,
          111,
          controller.signal
        );
        const storedForSeason = sheetGames.filter(
          (game) => getGameSeason(game) === Number(season)
        );

        setGames(mergeSeasonGames(storedForSeason, scheduleGames));
      } catch (requestError) {
        if (requestError.name === 'AbortError') return;

        const storedForSeason = sheetGames.filter(
          (game) => getGameSeason(game) === Number(season)
        );
        setGames(mergeSeasonGames(storedForSeason));
        setError(
          storedForSeason.length
            ? 'Live MLB schedule unavailable. Showing saved games.'
            : requestError.message || 'Season history could not be loaded.'
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadSeason();
    return () => controller.abort();
  }, [season, sheetGames]);

  useEffect(() => {
    document.title = `${season} Boston Red Sox Game History | Green Monster`;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }

    meta.content = `Complete ${season} Boston Red Sox game history with final scores, opponents, venues, and game recaps.`;
  }, [season]);

  const wins = useMemo(
    () => games.filter((game) => {
      const redSoxHome = game.home?.abbr === 'BOS';
      const awayWon = Number(game.away?.score) > Number(game.home?.score);
      const homeWon = Number(game.home?.score) > Number(game.away?.score);
      return redSoxHome ? homeWon : awayWon;
    }),
    [games]
  );

  const filtered = useMemo(() => {
    if (filter === 'Wins') return wins;
    if (filter === 'Losses') {
      const winIds = new Set(wins.map((game) => String(game.gamePk)));
      return games.filter((game) => !winIds.has(String(game.gamePk)));
    }
    return games;
  }, [filter, games, wins]);

  const downloadSitemap = () => {
    const xml = generateSitemap(games);
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = `red-sox-${season}-sitemap.xml`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const viewSitemap = () => {
    const blob = new Blob([generateSitemap(games)], {
      type: 'application/xml'
    });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="min-h-screen bg-[#1e3a29] p-3 sm:p-6 md:p-10">
      <main className="mx-auto flex max-w-5xl flex-col gap-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label="Back to scoreboard"
              className="border border-[#658d71] p-2 text-[#d8ceb3] transition-colors hover:bg-[#386646]"
            >
              <SafeIcon icon={FiArrowLeft} size={18} />
            </button>
            <div>
              <h1 className="text-xl font-black uppercase tracking-widest text-[#d8ceb3] sm:text-3xl">
                Game History
              </h1>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#658d71]">
                {season} · {games.length} games · {wins.length}W – {games.length - wins.length}L
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 border border-[#658d71] px-3 py-2">
              <SafeIcon icon={FiCalendar} size={12} className="text-[#658d71]" />
              <span className="text-[9px] font-black uppercase tracking-widest text-[#a9c9b1]">
                Season
              </span>
              <select
                value={season}
                onChange={(event) => {
                  setSeason(Number(event.target.value));
                  setFilter('All');
                }}
                className="bg-[#1e3a29] text-xs font-black text-white outline-none"
              >
                {seasons.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={() => setSeason((value) => value)}
              disabled={loading}
              className="flex items-center gap-1.5 border border-[#658d71] px-3 py-2 text-[9px] font-black uppercase tracking-widest text-[#a9c9b1] hover:text-white disabled:opacity-50"
            >
              <SafeIcon
                icon={FiRefreshCw}
                size={12}
                className={loading ? 'animate-spin' : ''}
              />
              Refresh
            </button>

            <button
              type="button"
              onClick={viewSitemap}
              disabled={!games.length}
              className="border border-[#658d71] p-2 text-[#a9c9b1] hover:text-white disabled:opacity-40"
              aria-label="View sitemap"
            >
              <SafeIcon icon={FiExternalLink} size={14} />
            </button>

            <button
              type="button"
              onClick={downloadSitemap}
              disabled={!games.length}
              className="border border-[#658d71] p-2 text-[#a9c9b1] hover:text-white disabled:opacity-40"
              aria-label="Download sitemap"
            >
              <SafeIcon icon={FiDownload} size={14} />
            </button>
          </div>
        </header>

        {error && (
          <div className="border border-[rgba(232,85,85,0.5)] bg-[rgba(232,85,85,0.08)] px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-[#e6a8a8]">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          {['All', 'Wins', 'Losses'].map((value) => (
            <button
              type="button"
              key={value}
              onClick={() => setFilter(value)}
              className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                filter === value
                  ? 'border-[#a9c9b1] bg-[#658d71] text-white'
                  : 'border-[#658d71] text-[#a9c9b1] hover:text-white'
              }`}
            >
              {value}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-4 py-24">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#658d71] border-t-[#d8ceb3]" />
            <span className="text-xs uppercase tracking-widest text-[#a9c9b1]">
              Loading the complete {season} season...
            </span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 border-2 border-[#658d71] py-24">
            <SafeIcon icon={FiDatabase} size={32} className="text-[#658d71]" />
            <p className="text-sm font-bold uppercase tracking-widest text-[#658d71]">
              No completed {filter.toLowerCase()} for {season}
            </p>
            <p className="max-w-sm text-center text-xs tracking-wider text-[#658d71]">
              Select another season or check back after the next official game finishes.
            </p>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {filtered.map((game, index) => (
              <motion.div
                key={game.gamePk}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.015, 0.3) }}
              >
                <GameHistoryCard game={game} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default GameHistoryPage;